import type { PaymentMethod, BrowserInfo } from '@adyen/adyen-web/dist/types/types'

// Schemas
import { paymentListSchema, receiptLinkSchema } from 'modules/pay/models'
import { revenueOrderSchema } from 'modules/usersongs/models'
import { sampleLicenseOrderSchema } from 'modules/licensing/models'
import { subscriptionOrderSchema, creditPackOrderSchema } from 'modules/subscription/models'

// Types
import { APIAction } from 'types'

export const actions = {
  fetchAvailablePaymentMethods: (): APIAction => ({
    CALL_API: {
      type: 'FETCH_AVAILABLE_PAYMENT_METHODS',
      method: 'GET',
      endpoint: '/api/payment/paymentmethods/',
    },
  }),

  addNewCard: (paymentMethod: PaymentMethod, browserInfo: BrowserInfo, returnUrl: string) => ({
    CALL_API: {
      type: 'ADD_NEW_CARD',
      method: 'POST',
      endpoint: '/api/payment/paymentmethods/',
      data: {
        payment_method: paymentMethod,
        path: returnUrl,
        browser_info: browserInfo,
      },
    },
  }),

  addExternalPaymentMethod: (paymentMethod: PaymentMethod) => ({
    CALL_API: {
      type: 'ADD_EXTERNAL_PAYMENT_METHOD',
      method: 'POST',
      endpoint: '/api/payment/paymentmethods/',
      data: { payment_method: paymentMethod },
    },
  }),

  saveExternalPaymentMethod: (paymentData: Record<string, unknown>) => ({
    CALL_API: {
      type: 'SAVE_EXTERNAL_PAYMENT_METHOD',
      method: 'POST',
      endpoint: '/api/payment/paymentmethods/details/',
      data: paymentData,
    },
  }),

  createSubscriptionOrder: (offerId: number, periodId: number): APIAction => ({
    CALL_API: {
      type: 'CREATE_SUBSCRIPTION_ORDER',
      method: 'POST',
      endpoint: '/api/subscriptions/subscriptionorder/',
      schema: subscriptionOrderSchema,
      data: {
        offer_id: offerId,
        period_id: periodId,
      },
    },
  }),

  paySubscriptionOrder: (subscriptionOrderId: number): APIAction => ({
    CALL_API: {
      type: 'PAY_SUBSCRIPTION_ORDER',
      method: 'POST',
      endpoint: `/api/subscriptions/subscriptionorder/${subscriptionOrderId}/pay/`,
      schema: subscriptionOrderSchema,
    },
  }),

  createCreditPackOrder: (offerId: number): APIAction => ({
    CALL_API: {
      type: 'CREATE_CREDIT_PACK_ORDER',
      method: 'POST',
      endpoint: '/api/subscriptions/creditpackorder/',
      schema: creditPackOrderSchema,
      data: {
        offer_id: offerId,
      },
    },
  }),

  payCreditPackOrder: (creditPackOrderId: number): APIAction => ({
    CALL_API: {
      type: 'PAY_CREDIT_PACK_ORDER',
      method: 'POST',
      endpoint: `/api/subscriptions/creditpackorder/${creditPackOrderId}/pay/`,
      schema: creditPackOrderSchema,
      requestKey: creditPackOrderId,
    },
  }),

  fetchSampleLicenseOrder: (sampleLicenseOrderId: number): APIAction => ({
    CALL_API: {
      type: 'FETCH_SAMPLE_LICENSE_ORDER',
      method: 'GET',
      endpoint: `/api/licensing/samplelicenseorder/${sampleLicenseOrderId}/`,
      schema: sampleLicenseOrderSchema,
    },
  }),

  paySampleLicenseOrder: (sampleLicenseOrderId: number): APIAction => ({
    CALL_API: {
      type: 'PAY_SAMPLE_LICENSE_ORDER',
      method: 'POST',
      endpoint: `/api/licensing/samplelicenseorder/${sampleLicenseOrderId}/pay/`,
      schema: sampleLicenseOrderSchema,
    },
  }),

  fetchPayments: (): APIAction => ({
    CALL_API: {
      type: 'FETCH_PAYMENTS',
      method: 'GET',
      endpoint: '/api/payment/',
      schema: paymentListSchema,
    },
  }),

  fetchReceiptLink: (paymentId: number): APIAction => ({
    CALL_API: {
      type: 'FETCH_RECEIPT_LINK',
      method: 'GET',
      endpoint: `/api/payment/receipt/${paymentId}/`,
      schema: receiptLinkSchema,
    },
  }),

  fetchRevenueOrder: (revenueOrderId: number): APIAction => ({
    CALL_API: {
      type: 'FETCH_REVENUE_ORDER',
      method: 'GET',
      endpoint: `/api/usersong/revenueorder/${revenueOrderId}/`,
      schema: revenueOrderSchema,
    },
  }),

  payRevenueOrder: (revenueOrderId: number): APIAction => ({
    CALL_API: {
      type: 'PAY_REVENUE_ORDER',
      method: 'POST',
      endpoint: `/api/usersong/revenueorder/${revenueOrderId}/pay/`,
      schema: revenueOrderSchema,
    },
  }),

  fetchLatestRevenueOrder: (): APIAction => ({
    CALL_API: {
      type: 'FETCH_LATEST_REVENUE_STREAM_ORDER',
      method: 'GET',
      endpoint: '/api/usersong/revenueorder/latest/',
      schema: revenueOrderSchema,
    },
  }),
}
